import { Bell, LogOut, Menu, Search, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { getInitials } from '@/lib/utils';

interface HeaderProps {
  onMenuClick?: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const { tenant, usage } = useTenant();

  return (
    <header className="bg-card border-b border-border px-4 py-4 lg:px-8">
      <div className="flex items-center justify-between">
        {/* Mobile menu button and search */}
        <div className="flex items-center space-x-4 flex-1 max-w-md">
          {/* Mobile menu button */}
          {onMenuClick && (
            <Button variant="ghost" size="icon" onClick={onMenuClick} className="lg:hidden">
              <Menu className="w-5 h-5" />
            </Button>
          )}

          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search packages..."
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md focus:ring-2 focus:ring-ring focus:border-input transition-colors"
            />
          </div>
        </div>

        {/* Usage indicator */}
        {usage && tenant?.subscription?.plan !== 'enterprise' && (
          <div className="hidden md:flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-3 bg-card px-3 py-2 rounded-md border border-border">
              <span className="text-gray-600 font-medium">Storage:</span>
              <span className="font-bold text-gray-900">{usage.storage.percentage.toFixed(0)}%</span>
              <div className="w-20 h-2 bg-gray-200/80 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300 rounded-full"
                  style={{
                    width: `${Math.min(usage.storage.percentage, 100)}%`,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Right side */}
        <div className="flex items-center space-x-3">
          {/* Notifications */}
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-destructive rounded-full" />
          </Button>

          {/* User menu */}
          <div className="flex items-center space-x-3 bg-card px-3 py-2 rounded-md border border-border">
            <div className="hidden md:block text-right">
              <div className="text-sm font-semibold text-gray-900">{user?.name}</div>
              <div className="text-xs text-gray-600">{user?.email}</div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Avatar */}
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm font-semibold">
                {user ? getInitials(user.name) : <User className="w-5 h-5" />}
              </div>

              {/* Logout */}
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                className="text-muted-foreground hover:text-foreground"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
